import { useEffect, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import styled from 'styled-components';

import { useVideoProjectCreation } from '@contexts/VideoProjectCreation.context';
import { formatTime } from '@utils/video/helpers';
import RangeSlider from './RangeSlider';
import Input from '@components/form/Input';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1rem;
	background: rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.1);
	border-radius: 0.375rem;
`;

const SectionLabel = styled.h4`
	margin: 0 0 0.5rem 0;
	font-size: 1rem;
	font-weight: 600;
`;

const TimeInputsGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1rem;
`;

const DurationInfo = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 0.5rem;
	padding: 0.75rem;
	background: rgba(0, 0, 0, 0.2);
	border-radius: 0.25rem;
	font-size: 0.875rem;
`;

const InfoItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;

	label {
		color: rgba(255, 255, 255, 0.6);
		font-size: 0.75rem;
		text-transform: uppercase;
	}

	value {
		color: #ffffff;
		font-weight: 600;
	}
`;

// Parse time string like "0:17" or "1:23" to seconds
const parseTimeString = (timeStr) => {
	if (timeStr == null || timeStr === '') return 0;
	
	// If it's already a number, return it
	if (typeof timeStr === 'number' && Number.isFinite(timeStr)) return timeStr;
	
	const normalized = String(timeStr).trim();
	if (!Number.isNaN(Number(normalized))) return Number(normalized);
	
	const parts = normalized.split(':').map(p => p.trim());
	
	// M:SS
	if (parts.length === 2) {
		const [minutes, seconds] = parts;
		return (Number(minutes) || 0) * 60 + (Number(seconds) || 0);
	}
	
	// H:MM:SS
	if (parts.length === 3) {
		const [hours, minutes, seconds] = parts;
		return (Number(hours) || 0) * 3600 + (Number(minutes) || 0) * 60 + (Number(seconds) || 0);
	}
	
	return 0;
};

const TimeRangeSelector = () => {
	const { videoRef, videoMetadata } = useVideoProjectCreation();
	const { control, watch, setValue } = useFormContext();

	const startSeconds = watch('timeRange.start_seconds');
	const endSeconds = watch('timeRange.end_seconds');
	const [currentTime, setCurrentTime] = useState(0);

	const duration = videoMetadata?.duration || 0;
	const selectedDuration = (endSeconds || duration) - (startSeconds || 0);

	// Sync with video playback
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		const handleTimeUpdate = () => {
			setCurrentTime(video.currentTime);

			// Only enforce range if user has explicitly changed the range
			// Don't enforce if it's still at default (0 to duration)
			const effectiveEnd = endSeconds || duration;
			const effectiveStart = startSeconds || 0;
			
			// Check if user has modified the range from defaults
			const isDefaultRange = effectiveStart === 0 && effectiveEnd === duration;
			
			// Only stop at end of range if user has customized it
			if (!isDefaultRange && effectiveEnd > 0 && video.currentTime >= effectiveEnd) {
				video.pause();
				video.currentTime = effectiveStart;
			}
		};

		video.addEventListener('timeupdate', handleTimeUpdate);
		return () => video.removeEventListener('timeupdate', handleTimeUpdate);
	}, [videoRef, startSeconds, endSeconds, duration]);

	const handleDragStart = () => {
		videoRef.current?.pause();
	};

	const handleStartChange = (newStart) => {
		const validStart = Math.max(0, Math.min(newStart, (endSeconds || duration) - 1));
		setValue('timeRange.start_seconds', validStart);
		if (videoRef.current) videoRef.current.currentTime = validStart;
	};

	const handleEndChange = (newEnd) => {
		const validEnd = Math.max((startSeconds || 0) + 1, Math.min(newEnd, duration));
		setValue('timeRange.end_seconds', validEnd);
		if (videoRef.current) videoRef.current.currentTime = validEnd;
	};

	return (
		<Container>
			<SectionLabel>Time Range Selection</SectionLabel>

			<RangeSlider
				min={0}
				max={duration}
				startValue={startSeconds || 0}
				endValue={endSeconds || duration}
				currentTime={currentTime}
				onStartChange={handleStartChange}
				onEndChange={handleEndChange}
				onDragStart={handleDragStart}
			/>

			<TimeInputsGrid>
				<Controller
					name="timeRange.start_seconds"
					control={control}
					rules={{
						required: 'Start time is required',
						min: { value: 0, message: 'Start time cannot be negative' },
						validate: {
							lessThanEnd: (value) =>
								(value ?? 0) < (endSeconds ?? duration) || 'Start time must be before end time',
						},
					}}
					render={({ field, fieldState: { error } }) => (
						<Input
							{...field}
							type="text"
							label="Start Time"
							placeholder="0:00"
							error={error?.message}
							value={formatTime(field.value ?? 0)}
							onChange={(e) => {
								const timeStr = e.target.value;
								const seconds = parseTimeString(timeStr);
								if (!isNaN(seconds)) {
									field.onChange(seconds);
									handleStartChange(seconds);
								}
							}}
						/>
					)}
				/>

				<Controller
					name="timeRange.end_seconds"
					control={control}
					rules={{
						required: 'End time is required',
						validate: {
							greaterThanStart: (value) =>
								(value ?? duration) > (startSeconds ?? 0) || 'End time must be after start time',
							withinDuration: (value) =>
								(value ?? duration) <= duration ||
								`End time cannot exceed video duration (${formatTime(duration)})`,
						},
					}}
					render={({ field, fieldState: { error } }) => (
						<Input
							{...field}
							type="text"
							label="End Time"
							placeholder="0:00"
							error={error?.message}
							value={formatTime(field.value ?? duration)}
							onChange={(e) => {
								const timeStr = e.target.value;
								const seconds = parseTimeString(timeStr);
								if (!isNaN(seconds)) {
									field.onChange(seconds);
									handleEndChange(seconds);
								}
							}}
						/>
					)}
				/>
			</TimeInputsGrid>
		</Container>
	);
};

export default TimeRangeSelector;
